/**
 * The Apple Pay window (GH-847): `/embed/give/<organizationId>/wallet`, opened by a framed give box.
 *
 * Apple Pay inside a frame on someone else's site needs that site's domain registered with Apple
 * for our merchant, which no host site is (docs/evidence/GH-847.md). This window is a first-party
 * GiveHere page on our own registered domain, so Apple Pay — and Google Pay — work in it exactly as
 * on the ordinary give page. The static server refuses to let it be framed.
 *
 * It asks the box that opened it for the gift (`givehere:wallet-ready` → `givehere:wallet-gift`,
 * same origin, from its opener only), re-quotes it on the server, shows the organization and the
 * server's total, and offers the wallet button — the donor's own press and the wallet's own sheet
 * are what authorize. The outcome goes back to the box (`givehere:wallet-result`, same origin) and
 * the window closes. Opened without a usable opener (a blocked popup reopened by hand, a link
 * copied elsewhere) it mints its own session from the address's hints and finishes here instead.
 */
import {
  formatMoney,
  parseEmbedOptions,
  parseEmbedWidgetId,
  type ContextResponse,
  type EmbedOptions,
} from "@givehere/contracts";
import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  createPaymentIntent,
  fetchContext,
  mintEmbedSession,
  newIdempotencyKey,
} from "../api.js";
import { sendableDesignationId } from "../DesignationPicker.js";
import { paymentKeys } from "../payment-keys.js";
import { stripeConfiguredInBundle } from "../StripeConfirm.js";
import { WalletExpressBoxes, type WalletMethod } from "../WalletExpress.js";
import {
  fromOwnWindow,
  type WalletGiftMessage,
  type WalletReadyMessage,
  type WalletResultMessage,
} from "./embed-frame.js";
import { useEmbedQuote } from "./embed-hooks.js";
import { useEmbedPresentation } from "./EmbedGive.js";
import { EmbedThanks } from "./EmbedThanks.js";
import "./embed.css";

/** How long to wait for the opening box to answer before standing alone. */
const HANDSHAKE_MS = 1500;

type Gift = {
  readonly token: string;
  readonly context: ContextResponse;
  readonly amountMinor: string | null;
  readonly costSupport: boolean;
  readonly designationId: string | null;
};

type GiftState =
  | { readonly state: "loading" }
  | { readonly state: "failed"; readonly message: string }
  | ({ readonly state: "ready" } & Gift);

/** The opener's gift if it answers, else one built from the address. */
function useWalletGift(organizationId: string, options: EmbedOptions): GiftState {
  const [gift, setGift] = useState<GiftState>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;
    const opener = window.opener as Window | null;
    const search = new URLSearchParams(window.location.search);
    const cover = search.get("cover") !== "0";

    const ready = async (token: string, choices: Omit<Gift, "token" | "context">) => {
      try {
        const context = await fetchContext(token);
        if (!cancelled) setGift({ state: "ready", token, context, ...choices });
      } catch (caught: unknown) {
        if (!cancelled) setGift({ state: "failed", message: messageOf(caught) });
      }
    };

    const standAlone = () => {
      // The saved widget it was opened from (GH-851), so a window stood up on its own is held to
      // the same rules on the server as the box that opened it.
      const widgetId = parseEmbedWidgetId(window.location.search);
      mintEmbedSession({
        organizationId,
        ...(options.eventId ? { eventId: options.eventId } : {}),
        ...(widgetId ? { widgetId } : {}),
      })
        .then((minted) => {
          if (minted.state === "unavailable") {
            if (!cancelled) {
              setGift({
                state: "failed",
                message: `${minted.organizationName} is not taking gifts here right now.`,
              });
            }
            return;
          }
          return ready(minted.token, {
            amountMinor: options.amountMinor,
            costSupport: cover,
            designationId: options.designationId,
          });
        })
        .catch((caught: unknown) => {
          if (!cancelled) setGift({ state: "failed", message: messageOf(caught) });
        });
    };

    const listen = (event: MessageEvent) => {
      if (!fromOwnWindow(event, opener)) return;
      const data = event.data as Partial<WalletGiftMessage> | null;
      if (!data || data.type !== "givehere:wallet-gift" || typeof data.contextToken !== "string") {
        return;
      }
      clearTimeout(timer);
      void ready(data.contextToken, {
        amountMinor: typeof data.amountMinor === "string" ? data.amountMinor : null,
        costSupport: data.costSupport !== false,
        designationId: typeof data.designationId === "string" ? data.designationId : null,
      });
    };

    window.addEventListener("message", listen);
    const timer = setTimeout(standAlone, opener ? HANDSHAKE_MS : 0);
    if (opener) {
      try {
        const asking: WalletReadyMessage = { type: "givehere:wallet-ready" };
        opener.postMessage(asking, window.location.origin);
      } catch {
        // The opener went away; the timer stands the window up on its own.
      }
    }
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener("message", listen);
    };
  }, [organizationId, options.eventId, options.amountMinor, options.designationId]);

  return gift;
}

const messageOf = (caught: unknown): string =>
  caught instanceof ApiError ? caught.message : "This window could not load the gift.";

export function EmbedWallet({ organizationId }: { organizationId: string }) {
  const [options] = useState(() => parseEmbedOptions(window.location.search));
  useEmbedPresentation(options);
  const gift = useWalletGift(organizationId, options);

  if (gift.state === "loading") {
    return (
      <main className="embed" aria-busy="true">
        <p className="embed__note">Loading…</p>
      </main>
    );
  }
  if (gift.state === "failed") {
    return (
      <main className="embed">
        <p className="embed__error" role="alert">
          {gift.message}
        </p>
      </main>
    );
  }
  return <WalletPay gift={gift} />;
}

function WalletPay({ gift }: { gift: Gift }) {
  const { context } = gift;
  const designationId = sendableDesignationId(context, gift.designationId);
  const { quote, quoting, error } = useEmbedQuote({
    token: gift.token,
    canQuote: Boolean(context.event?.acceptingDonations),
    amountMinor: gift.amountMinor,
    costSupport: gift.costSupport,
    designationId,
  });
  const keys = useRef(paymentKeys(newIdempotencyKey));
  const pending = useRef<WalletResultMessage | null>(null);
  const [done, setDone] = useState<WalletResultMessage | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const createIntent = async (method: WalletMethod) => {
    if (!quote) throw new Error("No quote");
    const created = await createPaymentIntent(
      { quoteToken: quote.quoteToken, displayedTotal: quote.total, chosenMethod: method },
      keys.current.keyFor(method, quote.quoteToken),
    );
    pending.current = {
      type: "givehere:wallet-result",
      donationId: created.donationId,
      accessToken: created.accessToken,
      total: quote.total,
    };
    if (!created.clientSecret) finish();
    return { clientSecret: created.clientSecret };
  };

  const finish = () => {
    const result = pending.current;
    if (!result) return;
    // Thanks here first, so a window the browser will not close never offers the button again.
    setDone(result);
    const opener = window.opener as Window | null;
    if (!opener) return;
    try {
      opener.postMessage(result, window.location.origin);
      window.close();
    } catch {
      // The box went away: the thanks above is the whole outcome.
    }
  };

  if (done) {
    return (
      <EmbedThanks
        paid={done}
        email=""
        organizationName={context.organization.displayName}
        total={done.total}
        onAgain={() => window.close()}
      />
    );
  }

  return (
    <main className="embed" aria-labelledby="wallet-org">
      <p className="embed__by">Secure giving by GiveHere</p>
      <h1 className="embed__org" id="wallet-org" dir="auto">
        {context.organization.displayName}
      </h1>
      {quote && (
        <p className="embed__total" aria-live="polite">
          <span>{quote.designationName ?? "Your gift"}</span>
          <span>{formatMoney(quote.total)}</span>
        </p>
      )}
      {quote && quote.costSupportSelected && (
        <p className="embed__note">
          Includes {formatMoney(quote.costSupport)} {quote.costSupportShortLabel}.
        </p>
      )}
      {(error || !gift.amountMinor) && (
        <p className="embed__error" role="alert">
          {error ?? "Choose an amount in the give box first."}
        </p>
      )}
      {quote && !quoting && stripeConfiguredInBundle() && (
        <WalletExpressBoxes
          amountMinor={quote.total.amountMinor}
          currency={quote.total.currency}
          createIntent={createIntent}
          onAuthorized={finish}
          onFailed={() => keys.current.reset()}
          onAvailability={(present) => setUnavailable(!present)}
        />
      )}
      {unavailable && (
        <p className="embed__note">
          No Apple Pay or Google Pay is set up here. Close this window and give by card instead.
        </p>
      )}
    </main>
  );
}
